// ============================================================================
// STATISTIQUES › STOCK › REPORTING TAUX DE SORTIE — ce qui est entré, ce qui est sorti, et la part écoulée, sur les niveaux
// que l'on choisit ; les rétrocessions comptent, ou pas. Réf. maquette du back-office (écran `rts`, « Statistiques ·
// Reporting taux de sortie ») et docs/CADRAGE-Statistiques-Stock.md §11.
//
// ⚠️ L'ÉCRAN TRIE ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : le serveur rend les DEUX taux (rétrocessions exclues et
//    comptées, `sales/stats-stock-report.ts`) — le sélecteur choisit celui qu'on lit, il ne le calcule pas — et les seuils
//    de couleur, ceux du cœur partout (décision utilisateur ; le dessin écrit 70 / 35 %).
// ⚠️ LES RÉTROCESSIONS SONT COMPTÉES PAR DÉFAUT (décision utilisateur), comme le dessin : exclues, le taux est celui du
//    Résumé, et le pied le dit.
// ⚠️ UNE VALEUR NON MESURABLE S'AFFICHE « — », JAMAIS ZÉRO.
// ============================================================================
import type { ReactNode } from 'react';
import { Trans } from '@orgazone/ui-web';
import type { SalesSummaryFilterKey, StockExitMeasures, StockExitMetric, StockExitValues, StockExitView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { exporterCsv } from './export-csv';
import { FILTER_DIMS, dimLabel } from './stats-bar';
import { csvQty, csvRate, dTx, pc, qty, shownValue } from './stats-format';
import { StatsGrid, sortStatsRows, type GridCell, type StatsSort } from './stats-grid';
import type { ReportDisplay } from './stats-reporting';

// ─── Les colonnes ────────────────────────────────────────────────────────────────────────────────

/** Les rétrocessions : comptées dans les sorties, ou exclues. */
export type RetroMode = 'in' | 'out';

/** La colonne du taux suit le sélecteur : `rate` désigne celui qu'on lit. */
type ColumnKey = 'soldQty' | 'receivedQty' | 'retroQty' | 'stockQty' | 'rate';

/** Le tri d'ouverture : l'ordre des niveaux, à égalité le plus vendu d'abord. */
export const STOCK_EXIT_DEFAULT_SORT: StatsSort = { k: 'rate', d: -1, man: false };

interface ExitColumn {
  k: ColumnKey;
  label: Translatable;
  unit: 'qty' | 'rate';
  /** La largeur de la maquette ; la colonne s'élargit si son libellé ne tient pas. */
  width: number;
  color: string;
}

/** Les cinq colonnes du dessin, toutes affichées : il n'offre pas de bouton « Colonnes » ici. */
const COLUMNS: ExitColumn[] = [
  { k: 'soldQty', label: key('Ventes'), unit: 'qty', width: 96, color: 'var(--teal-500)' },
  { k: 'receivedQty', label: key('Entrées en stock'), unit: 'qty', width: 122, color: 'var(--blue)' },
  { k: 'retroQty', label: key('Rétrocessions'), unit: 'qty', width: 114, color: 'var(--violet)' },
  { k: 'stockQty', label: key('Stock restant'), unit: 'qty', width: 108, color: 'var(--teal-500)' },
  { k: 'rate', label: key('Taux de sortie'), unit: 'rate', width: 116, color: 'var(--teal-500)' },
];
/** Les colonnes que l'onglet sait trier — une vue enregistrée n'en reprend pas d'autre. */
export const STOCK_EXIT_SORT_KEYS: readonly string[] = COLUMNS.map((c) => c.k);

/** L'indicateur qu'une colonne lit, selon le sélecteur. */
const metricOf = (k: ColumnKey, retro: RetroMode): StockExitMetric =>
  k === 'rate' ? (retro === 'in' ? 'exitRateBp' : 'sellThroughBp') : k;

/** La légende de la ligne LIGNES : les trois flux, et ce que le sélecteur fait des rétrocessions. */
export const stockExitLegend = (retro: RetroMode): { label: string; color: string }[] => [
  { label: t('Ventes'), color: 'var(--teal-500)' },
  { label: t('Entrées'), color: 'var(--blue)' },
  { label: retro === 'in' ? t('Rétrocessions comptées') : t('Rétrocessions exclues'), color: 'var(--violet)' },
];

/** « Rétrocessions : Comptées · Exclues » — dans l'en-tête de la page, à côté de l'export. */
export function RetroModeToggle({ value, onChange }: { value: RetroMode; onChange: (v: RetroMode) => void }) {
  const items: { v: RetroMode; l: string; title: string }[] = [
    { v: 'in', l: t('Comptées'), title: t('Une pièce partie vers un autre magasin est sortie du rayon : elle compte') },
    { v: 'out', l: t('Exclues'), title: t('Seules les ventes comptent : la rétrocession n’est pas une vente') },
  ];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
        whiteSpace: 'nowrap',
      }}>{t('Rétrocessions')}</span>
      <span role="group" aria-label={t('Rétrocessions')} style={{
        display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)',
      }}>
        {items.map((x) => {
          const on = x.v === value;
          return (
            <button key={x.v} type="button" aria-pressed={on} title={x.title} onClick={() => onChange(x.v)} style={{
              height: 28, padding: '0 11px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'transparent',
              color: on ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
              fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
            }}>{x.l}</button>
          );
        })}
      </span>
    </span>
  );
}

// ─── Le tri ──────────────────────────────────────────────────────────────────────────────────────

type Row = StockExitView['rows'][number];

/** Les indicateurs d'une fenêtre — `null` quand la fenêtre n'est pas lue (pas de comparaison). */
const windowOf = (v: StockExitValues, display: ReportDisplay): StockExitMeasures | null => (display === 'b' ? v.b : v.a);

/** La valeur d'une ligne pour une colonne et un affichage — `null` quand elle ne se mesure pas. */
function cellValue(v: StockExitValues, k: ColumnKey, display: ReportDisplay, retro: RetroMode): number | null {
  const m = metricOf(k, retro);
  if (display === 'd') return v.growth?.[m] ?? null;
  return windowOf(v, display)?.[m] ?? null;
}

/** Les lignes dans l'ordre demandé — à égalité de niveaux, le plus vendu d'abord (une colonne affichée). */
export function sortStockExitRows(rows: Row[], axes: SalesSummaryFilterKey[], sort: StatsSort, display: ReportDisplay, retro: RetroMode, lang: string): Row[] {
  return sortStatsRows(rows, axes.length, sort, (r, k) => cellValue(r, k as ColumnKey, display, retro), (x, y) => y.a.soldQty - x.a.soldQty, lang);
}

// ─── Les cellules ────────────────────────────────────────────────────────────────────────────────

const UNKNOWN: GridCell = { text: '—', fg: 'var(--ink-300)', fw: 600 };

/**
 * Le texte, la teinte et la graisse d'une cellule : le taux coloré par les seuils SERVIS (vert au-dessus du seuil rapide,
 * ambre sous le seuil lent), une rétrocession en violet ; en Écart, l'écart relatif, vert au-dessus de +0,5 %, rose sous
 * −0,5 %.
 */
function cell(v: StockExitValues, c: ExitColumn, display: ReportDisplay, retro: RetroMode, th: StockExitView['thresholds']): GridCell {
  if (display === 'd') {
    const g = cellValue(v, c.k, 'd', retro);
    if (g === null) return UNKNOWN;
    const flat = Math.abs(g) < 50;
    return { text: dTx(g), fg: flat ? 'var(--ink-500)' : g > 0 ? 'var(--teal-800)' : 'var(--rose-fg)', fw: 700 };
  }
  if (!windowOf(v, display)) return UNKNOWN;
  const x = cellValue(v, c.k, display, retro);
  const fg = x === null ? 'var(--ink-300)'
    : c.k === 'rate' ? (x >= th.fastSellThroughBp ? 'var(--teal-800)' : x < th.slowSellThroughBp ? 'var(--amber-fg)' : 'var(--ink-800)')
      : c.k === 'retroQty' && x > 0 ? 'var(--violet-fg)' : 'var(--ink-800)';
  return { text: c.unit === 'rate' ? pc(x) : qty(x), fg, fw: c.k === 'rate' ? 700 : 600 };
}

const columnLabel = (c: ExitColumn): string => t(c.label);

/** L'infobulle d'un en-tête : celle du taux dit ce qu'il compte, selon le sélecteur. */
const columnTitle = (c: ExitColumn, retro: RetroMode): string | undefined => (c.k !== 'rate' ? undefined
  : retro === 'in' ? t('Ventes + rétrocessions rapportées à ce qu’on a eu en main') : t('Ventes seules rapportées à ce qu’on a eu en main'));

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/**
 * LE TABLEAU DU REPORTING TAUX DE SORTIE — ses colonnes et ses cellules, dans le squelette commun des reportings.
 * `filtered` dit si un filtre est posé : l'état vide ne dit pas la même chose avec ou sans.
 */
export function StockExitTable({ view, display, retro, sort, onSort, filtered, onReset, lang, busy = false }: {
  view: StockExitView;
  display: ReportDisplay;
  retro: RetroMode;
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
}) {
  const axes = view.axes;
  const shownDisplay: ReportDisplay = view.reference ? display : 'a';
  const rows = sortStockExitRows(view.rows, axes, sort, shownDisplay, retro, lang);
  const axisDim = (k: SalesSummaryFilterKey) => FILTER_DIMS.find((d) => d.k === k)!;
  const totalSub = [
    ...(shownDisplay === 'd' ? [t('écart A/B')] : shownDisplay === 'b' ? [t('période B')] : []),
    t('{n} ligne', { n: view.rows.length }),
    ...(sort.man ? [] : [t('ordre de la ventilation')]),
  ].join(' · ');
  const strong = (x: ReactNode) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>;

  return (
    <StatsGrid label={t('Reporting taux de sortie')} sort={sort} onSort={onSort} busy={busy} headColor="var(--ink-800)"
      lastAxis={{ min: 170, fr: 1.4 }}
      axes={axes.map((k) => ({ label: dimLabel(axisDim(k)), code: k === 'art' }))}
      columns={COLUMNS.map((c) => ({ k: c.k, label: columnLabel(c), width: c.width, color: c.color, title: columnTitle(c, retro), fixed: true }))}
      total={{ sub: totalSub, cells: COLUMNS.map((c) => cell(view.total, c, shownDisplay, retro, view.thresholds)) }}
      rows={rows.map((r) => ({
        key: r.parts.map((p) => p.value).join('\u001f'), parts: r.parts, sub: t('{n} article', { n: r.articles }),
        cells: COLUMNS.map((c) => cell(r, c, shownDisplay, retro, view.thresholds)),
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Ni stock ni mouvement sur cette période')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez une autre période, ou comparez-la à une autre.')}</div>
                </>
              )}
            </div>
          )}
          <div style={{
            padding: '10px 16px', background: 'var(--ink-50)', borderRadius: '0 0 var(--r-sm) var(--r-sm)', fontSize: 'var(--t-meta)',
            color: 'var(--ink-500)', lineHeight: 1.55,
          }}>
            <Trans k='Taux de sortie = <1>sorties / (sorties + stock restant)</1> : la part de ce qu’on a eu en main qui est effectivement partie. C’est un <2>taux</2> — il se recalcule sur les sommes, jamais en moyennant des pourcentages de lignes. Les <3>rétrocessions</3> se comptent ou s’excluent selon le sélecteur : comptées, on mesure ce qui a quitté le rayon ; exclues, on mesure ce qui s’est vendu ici, et c’est le taux de sortie du Résumé. « Entrées en stock » compte le reçu — les entrées achetées ; le stock restant est celui de la fin de la période. Une valeur non mesurable affiche « — », jamais zéro.'
              parts={[strong, strong, strong]} />
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les niveaux, les cinq colonnes, le tri, la ligne de total — et le taux que le sélecteur
 * montre, nommé dans son en-tête.
 * ⚠️ UN NOMBRE INCONNU RESTE VIDE dans le fichier — un 0 dans un tableur devient une somme fausse.
 */
export function exportStockExit(view: StockExitView, display: ReportDisplay, retro: RetroMode, sort: StatsSort, lang: string): void {
  const axes = view.axes;
  const shownDisplay: ReportDisplay = view.reference ? display : 'a';
  const label = (c: ExitColumn): string => {
    const base = c.k === 'rate' ? (retro === 'in' ? t('Taux de sortie, rétrocessions comptées') : t('Taux de sortie, rétrocessions exclues')) : columnLabel(c);
    return shownDisplay === 'd' || c.unit === 'rate' ? `${base} (%)` : base;
  };
  // ⚠️ LE FICHIER DIT CE QU'IL CONTIENT : une comparaison ou un écart, intitulés comme la période, en seraient
  //    indiscernables une fois ouverts.
  const head = (c: ExitColumn) => (shownDisplay === 'b' ? t('{v} — période B', { v: label(c) })
    : shownDisplay === 'd' ? t('{v} — écart A/B', { v: label(c) }) : label(c));
  const value = (v: StockExitValues, c: ExitColumn): string => {
    const x = cellValue(v, c.k, shownDisplay, retro);
    if (x === null) return '';
    return shownDisplay === 'd' || c.unit === 'rate' ? csvRate(x) : csvQty(x);
  };
  const ref = view.reference;
  const fileName = shownDisplay === 'b' && ref ? `reporting-taux-de-sortie-comparaison-${ref.from}-${ref.to}`
    : shownDisplay === 'd' && ref ? `reporting-taux-de-sortie-ecart-${view.period.from}-${view.period.to}-vs-${ref.from}-${ref.to}`
      : `reporting-taux-de-sortie-${view.period.from}-${view.period.to}`;
  const rows = sortStockExitRows(view.rows, axes, sort, shownDisplay, retro, lang);
  exporterCsv(fileName,
    [...axes.map((k) => dimLabel(FILTER_DIMS.find((d) => d.k === k)!)), t('Articles'), ...COLUMNS.map(head)],
    [
      [t('TOTAL'), ...axes.slice(1).map(() => ''), String(view.scope.references), ...COLUMNS.map((c) => value(view.total, c))],
      ...rows.map((r) => [
        ...r.parts.map((p) => (p.label !== p.value ? `${p.value} — ${shownValue(p.label)}` : shownValue(p.value))), String(r.articles),
        ...COLUMNS.map((c) => value(r, c)),
      ]),
    ]);
}
